<template>
  <div class="container-fluid py-3">
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-header bg-white py-3">
        <div class="row align-items-center">
          <div class="col-md-6 col-12 mb-2 mb-md-0">
            <h4 class="mb-0 text-primary font-weight-bold">
              <i class="fa fa-bar-chart me-2"></i>Relatórios Financeiros
            </h4>
            <small class="text-muted">
              {{ moduloAtivo === 'caixinha' ? 'Caixinha (Prestação de Contas)' : 'Pagamentos Banco do Brasil' }} — {{ nomeCapsSelecionado }}
            </small>
          </div>

          <!-- Filtros de Ano e CAPS -->
          <div class="col-md-6 col-12">
            <div class="row g-2 justify-content-md-end align-items-center">
              <!-- Filtro de CAPS (visível para adm) -->
              <div class="col-sm-5 col-6" v-if="userJson.nivel_acesso == 'adm'">
                <label class="form-label mb-0 small text-muted font-weight-bold">CAPS</label>
                <select
                  class="form-control form-control-sm"
                  v-model="caps_id"
                  @change="carregarRelatorio"
                  :disabled="loading"
                >
                  <option v-for="c in capsList" :key="c.id" :value="c.id">
                    {{ c.nome }}
                  </option>
                </select>
              </div>

              <!-- Filtro de Ano -->
              <div class="col-sm-4 col-6">
                <label class="form-label mb-0 small text-muted font-weight-bold">Ano</label>
                <select
                  class="form-control form-control-sm"
                  v-model="ano"
                  @change="carregarRelatorio"
                  :disabled="loading"
                >
                  <option v-for="y in anosDisponiveis" :key="y" :value="y">
                    {{ y }}
                  </option>
                </select>
              </div>

              <!-- Botão Atualizar -->
              <div class="col-sm-3 col-12 d-flex align-items-end">
                <button
                  type="button"
                  class="btn btn-primary btn-sm w-100 mt-2 mt-sm-0"
                  @click="carregarRelatorio"
                  :disabled="loading"
                >
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <span v-else><i class="fa fa-refresh me-1"></i></span>
                  Filtrar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-body">
        <!-- SELETOR DE MÓDULO: Caixinha vs Banco do Brasil -->
        <ul class="nav nav-pills mb-4 p-1 bg-light rounded shadow-sm" role="tablist">
          <li class="nav-item flex-fill text-center">
            <button
              class="nav-link w-100 border-0 py-2"
              :class="moduloAtivo === 'caixinha' ? 'active shadow-sm font-weight-bold' : 'text-muted'"
              @click="mudarModulo('caixinha')"
            >
              <i class="fa fa-money me-1"></i> Relatório de Caixinha
            </button>
          </li>
          <li class="nav-item flex-fill text-center">
            <button
              class="nav-link w-100 border-0 py-2"
              :class="moduloAtivo === 'bb' ? 'active shadow-sm font-weight-bold' : 'text-muted'"
              @click="mudarModulo('bb')"
            >
              <i class="fa fa-university me-1"></i> Gastos Banco do Brasil (BB)
            </button>
          </li>
        </ul>

        <!-- ============================================================= -->
        <!-- MÓDULO 1: CAIXINHA -->
        <!-- ============================================================= -->
        <div v-show="moduloAtivo === 'caixinha'">
          <!-- Cards de Resumo Caixinha (KPIs) -->
          <div class="row mb-4">
            <div class="col-md-4 col-12 mb-3 mb-md-0">
              <div class="card border-success border-start border-4 shadow-sm h-100">
                <div class="card-body py-2">
                  <div class="text-muted small text-uppercase font-weight-bold">Total Entradas ({{ ano }})</div>
                  <div class="h4 mb-0 font-weight-bold text-success">
                    {{ formatarMoeda(totais.entradas) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-4 col-12 mb-3 mb-md-0">
              <div class="card border-danger border-start border-4 shadow-sm h-100">
                <div class="card-body py-2">
                  <div class="text-muted small text-uppercase font-weight-bold">Total Saídas ({{ ano }})</div>
                  <div class="h4 mb-0 font-weight-bold text-danger">
                    {{ formatarMoeda(totais.saidas) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-4 col-12">
              <div
                class="card border-start border-4 shadow-sm h-100"
                :class="totais.saldo >= 0 ? 'border-primary' : 'border-warning'"
              >
                <div class="card-body py-2">
                  <div class="text-muted small text-uppercase font-weight-bold">Saldo do Período</div>
                  <div
                    class="h4 mb-0 font-weight-bold"
                    :class="totais.saldo >= 0 ? 'text-primary' : 'text-danger'"
                  >
                    {{ formatarMoeda(totais.saldo) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- PRIMEIRO GRÁFICO: Fluxo Mensal Caixinha -->
          <div class="card border-0 bg-light p-3 mb-4 shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 class="mb-0 text-secondary font-weight-bold">
                  1. Fluxo Financeiro Mensal de Caixinha: Entradas vs Saídas
                </h6>
                <small class="text-muted">Distribuição mês a mês no exercício {{ ano }}</small>
              </div>
              <span class="badge bg-secondary">Exercício {{ ano }}</span>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2 text-muted">Carregando dados financeiros...</p>
            </div>

            <!-- Container do Gráfico BarChart 1 -->
            <div v-show="!loading" style="position: relative; height: 360px; width: 100%;">
              <bar-chart
                v-if="loaded"
                :key="chartKey"
                :chart-data="datacollection"
                :options="chartOptions"
              />
            </div>
          </div>

          <!-- Tabela Resumo Mensal Detalhada Caixinha -->
          <div class="card border-0 shadow-sm mb-5">
            <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold text-secondary">
                Detalhamento Mensal Caixinha ({{ ano }})
              </h6>
              <button
                class="btn btn-sm btn-link text-decoration-none p-0"
                type="button"
                @click="mostrarTabelaMensal = !mostrarTabelaMensal"
              >
                {{ mostrarTabelaMensal ? 'Ocultar tabela' : 'Exibir tabela' }}
              </button>
            </div>
            <div class="table-responsive" v-show="mostrarTabelaMensal">
              <table class="table table-hover table-striped align-middle mb-0 text-center">
                <thead class="table-light">
                  <tr style="font-size: 13px;">
                    <th class="text-start ps-3">Mês</th>
                    <th class="text-success text-end pe-3">Entradas</th>
                    <th class="text-danger text-end pe-3">Saídas</th>
                    <th class="text-end pe-3">Saldo Mês</th>
                  </tr>
                </thead>
                <tbody style="font-size: 13px;">
                  <tr v-for="item in dadosMensais" :key="item.rotulo">
                    <td class="text-start ps-3 font-weight-bold">{{ item.mesNome }} ({{ item.rotulo }})</td>
                    <td class="text-success text-end pe-3 font-weight-bold">{{ formatarMoeda(item.entradas) }}</td>
                    <td class="text-danger text-end pe-3 font-weight-bold">{{ formatarMoeda(item.saidas) }}</td>
                    <td
                      class="text-end pe-3 font-weight-bold"
                      :class="item.saldo >= 0 ? 'text-primary' : 'text-danger'"
                    >
                      {{ formatarMoeda(item.saldo) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-light font-weight-bold" style="font-size: 13px;">
                  <tr>
                    <td class="text-start ps-3">TOTAL GERAL</td>
                    <td class="text-success text-end pe-3">{{ formatarMoeda(totais.entradas) }}</td>
                    <td class="text-danger text-end pe-3">{{ formatarMoeda(totais.saidas) }}</td>
                    <td
                      class="text-end pe-3"
                      :class="totais.saldo >= 0 ? 'text-primary' : 'text-danger'"
                    >
                      {{ formatarMoeda(totais.saldo) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- SEGUNDO GRÁFICO: Detalhamento por Descrição e Centro de Custo Caixinha -->
          <div class="card border-0 bg-light p-3 mb-4 shadow-sm">
            <div class="row align-items-center mb-3">
              <div class="col-md-5 col-12 mb-2 mb-md-0">
                <h6 class="mb-0 text-secondary font-weight-bold">
                  2. Detalhamento de Despesas de Caixinha: {{ visaoDetalhe === 'descricao' ? 'Por Descrição' : 'Por Centro de Custo' }}
                </h6>
                <small class="text-muted">
                  Total acumulado de despesas na tabela lan_fiscal_caixinhas ({{ ano }})
                </small>
              </div>

              <!-- Controles de alternância e filtros do segundo gráfico -->
              <div class="col-md-7 col-12">
                <div class="row g-2 justify-content-md-end align-items-center">
                  <!-- Seletor de visualização (Por Descrição vs Por Centro de Custo) -->
                  <div class="col-auto">
                    <div class="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        class="btn"
                        :class="visaoDetalhe === 'descricao' ? 'btn-primary font-weight-bold' : 'btn-outline-primary'"
                        @click="alterarVisao('descricao')"
                      >
                        <i class="fa fa-tag me-1"></i>Por Descrição
                      </button>
                      <button
                        type="button"
                        class="btn"
                        :class="visaoDetalhe === 'centro_custo' ? 'btn-primary font-weight-bold' : 'btn-outline-primary'"
                        @click="alterarVisao('centro_custo')"
                      >
                        <i class="fa fa-building-o me-1"></i>Por Centro de Custo
                      </button>
                    </div>
                  </div>

                  <!-- Filtro de Centro de Custo quando na visão por Descrição -->
                  <div class="col-auto" v-if="visaoDetalhe === 'descricao' && listaCentrosCustoUnicos.length > 0">
                    <select
                      class="form-control form-control-sm"
                      v-model="filtroCentroCusto"
                      @change="atualizarGraficoDetalhes"
                      title="Filtrar por centro de custo específico"
                    >
                      <option value="">Todos os Centros de Custo</option>
                      <option v-for="cc in listaCentrosCustoUnicos" :key="cc" :value="cc">
                        CC: {{ cc }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading Spinner do Segundo Gráfico -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status" style="width: 2.5rem; height: 2.5rem;">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2 text-muted">Carregando detalhamento de despesas...</p>
            </div>

            <!-- Sem dados -->
            <div
              v-if="!loading && (!detalheCollection || !detalheCollection.labels || detalheCollection.labels.length === 0)"
              class="text-center py-5 text-muted"
            >
              <i class="fa fa-info-circle fa-2x mb-2 text-secondary"></i>
              <p class="mb-0">Nenhuma despesa registrada para os filtros selecionados no ano de {{ ano }}.</p>
            </div>

            <!-- Container do Gráfico BarChart 2 -->
            <div
              v-show="!loading && detalheCollection && detalheCollection.labels && detalheCollection.labels.length > 0"
              style="position: relative; height: 380px; width: 100%;"
            >
              <bar-chart
                v-if="loadedDetalhe"
                :key="'detalhe-' + chartDetalheKey"
                :chart-data="detalheCollection"
                :options="chartDetalheOptions"
              />
            </div>
          </div>

          <!-- Tabela Analítica Caixinha -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold text-secondary">
                Tabela Analítica de Caixinha: Despesas {{ visaoDetalhe === 'descricao' ? 'por Descrição' : 'por Centro de Custo' }} ({{ ano }})
              </h6>
              <div style="width: 240px;">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Buscar na tabela..."
                  v-model="filtroTabelaBusca"
                />
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-hover table-striped align-middle mb-0 text-center">
                <thead class="table-light">
                  <tr style="font-size: 13px;">
                    <th class="text-start ps-3">
                      {{ visaoDetalhe === 'descricao' ? 'Descrição' : 'Centro de Custo' }}
                    </th>
                    <th class="text-center" v-if="visaoDetalhe === 'descricao'">Centro de Custo</th>
                    <th class="text-center">Qtd. Lançamentos</th>
                    <th class="text-end pe-3">Total Gasto</th>
                    <th class="text-end pe-3">% do Total de Saídas</th>
                  </tr>
                </thead>
                <tbody style="font-size: 13px;">
                  <tr v-for="(item, idx) in itensTabelaDetalhe" :key="idx">
                    <td class="text-start ps-3 font-weight-bold">
                      <span class="badge me-1" :style="{ backgroundColor: getCorBadge(idx) }">&nbsp;</span>
                      {{ item.titulo }}
                    </td>
                    <td class="text-center" v-if="visaoDetalhe === 'descricao'">
                      <span class="badge bg-light text-dark border">{{ item.centro_custo }}</span>
                    </td>
                    <td class="text-center">{{ item.quantidade }}</td>
                    <td class="text-end pe-3 font-weight-bold text-danger">
                      {{ formatarMoeda(item.total) }}
                    </td>
                    <td class="text-end pe-3">
                      <div class="d-flex align-items-center justify-content-end">
                        <span class="me-2 font-weight-bold">{{ item.percentual }}%</span>
                        <div class="progress" style="width: 60px; height: 6px;">
                          <div
                            class="progress-bar bg-danger"
                            role="progressbar"
                            :style="{ width: item.percentual + '%' }"
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="itensTabelaDetalhe.length === 0">
                    <td :colspan="visaoDetalhe === 'descricao' ? 5 : 4" class="py-3 text-muted">
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-light font-weight-bold" style="font-size: 13px;">
                  <tr>
                    <td class="text-start ps-3">TOTAL DAS DESPESAS EXIBIDAS</td>
                    <td v-if="visaoDetalhe === 'descricao'"></td>
                    <td class="text-center">{{ totalQtdExibida }}</td>
                    <td class="text-danger text-end pe-3">{{ formatarMoeda(totalSomaExibida) }}</td>
                    <td class="text-end pe-3">{{ totalPercentualExibido }}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <!-- ============================================================= -->
        <!-- MÓDULO 2: BANCO DO BRASIL (BB) -->
        <!-- ============================================================= -->
        <div v-show="moduloAtivo === 'bb'">
          <!-- Card de Resumo BB (KPI) -->
          <div class="row mb-4">
            <div class="col-md-6 col-12 mb-3 mb-md-0">
              <div class="card border-primary border-start border-4 shadow-sm h-100">
                <div class="card-body py-3">
                  <div class="text-muted small text-uppercase font-weight-bold">
                    <i class="fa fa-university me-1 text-primary"></i>Total de Gastos Banco do Brasil ({{ ano }})
                  </div>
                  <div class="h3 mb-0 font-weight-bold text-primary mt-1">
                    {{ formatarMoeda(bbRaw.mensal.total_ano) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6 col-12">
              <div class="card border-info border-start border-4 shadow-sm h-100">
                <div class="card-body py-3">
                  <div class="text-muted small text-uppercase font-weight-bold">
                    <i class="fa fa-file-text-o me-1 text-info"></i>Total Lançamentos Fiscais BB
                  </div>
                  <div class="h3 mb-0 font-weight-bold text-info mt-1">
                    {{ totalQtdExibidaBB }} comprovantes rateados
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- GRÁFICO 1 BB: Evolução Mensal BB -->
          <div class="card border-0 bg-light p-3 mb-4 shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 class="mb-0 text-secondary font-weight-bold">
                  1. Evolução Mensal dos Gastos: Banco do Brasil
                </h6>
                <small class="text-muted">Total de pagamentos mês a mês no exercício {{ ano }}</small>
              </div>
              <span class="badge bg-primary">Banco do Brasil — {{ ano }}</span>
            </div>

            <!-- Loading Spinner BB -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2 text-muted">Carregando dados do Banco do Brasil...</p>
            </div>

            <!-- Container Gráfico Mensal BB -->
            <div v-show="!loading" style="position: relative; height: 360px; width: 100%;">
              <bar-chart
                v-if="loadedBb"
                :key="'bb-mensal-' + chartBbMensalKey"
                :chart-data="bbMensalCollection"
                :options="chartBbMensalOptions"
              />
            </div>
          </div>

          <!-- GRÁFICO 2 BB: Detalhamento por Descrição (lan_descricaos) & Centro de Custo (lan_fiscais) -->
          <div class="card border-0 bg-light p-3 mb-4 shadow-sm">
            <div class="row align-items-center mb-3">
              <div class="col-md-5 col-12 mb-2 mb-md-0">
                <h6 class="mb-0 text-secondary font-weight-bold">
                  2. Detalhamento de Gastos BB: {{ bbVisaoDetalhe === 'descricao' ? 'Por Descrição (lan_descricaos)' : 'Por Centro de Custo (lan_fiscais)' }}
                </h6>
                <small class="text-muted">
                  Comparativo anual das despesas do Banco do Brasil ({{ ano }})
                </small>
              </div>

              <!-- Controles de alternância e filtros do gráfico BB -->
              <div class="col-md-7 col-12">
                <div class="row g-2 justify-content-md-end align-items-center">
                  <!-- Seletor de visualização (Por Descrição vs Por Centro de Custo) -->
                  <div class="col-auto">
                    <div class="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        class="btn"
                        :class="bbVisaoDetalhe === 'descricao' ? 'btn-primary font-weight-bold' : 'btn-outline-primary'"
                        @click="alterarVisaoBB('descricao')"
                      >
                        <i class="fa fa-tag me-1"></i>Por Descrição
                      </button>
                      <button
                        type="button"
                        class="btn"
                        :class="bbVisaoDetalhe === 'centro_custo' ? 'btn-primary font-weight-bold' : 'btn-outline-primary'"
                        @click="alterarVisaoBB('centro_custo')"
                      >
                        <i class="fa fa-building-o me-1"></i>Por Centro de Custo
                      </button>
                    </div>
                  </div>

                  <!-- Filtro de Centro de Custo quando na visão por Descrição -->
                  <div class="col-auto" v-if="bbVisaoDetalhe === 'descricao' && listaCentrosCustoUnicosBB.length > 0">
                    <select
                      class="form-control form-control-sm"
                      v-model="bbFiltroCentroCusto"
                      @change="atualizarGraficosBB"
                      title="Filtrar por centro de custo específico"
                    >
                      <option value="">Todos os Centros de Custo</option>
                      <option v-for="cc in listaCentrosCustoUnicosBB" :key="cc" :value="cc">
                        CC: {{ cc }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading Spinner BB -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status" style="width: 2.5rem; height: 2.5rem;">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2 text-muted">Carregando detalhamento de gastos BB...</p>
            </div>

            <!-- Sem dados BB -->
            <div
              v-if="!loading && (!bbDetalheCollection || !bbDetalheCollection.labels || bbDetalheCollection.labels.length === 0)"
              class="text-center py-5 text-muted"
            >
              <i class="fa fa-info-circle fa-2x mb-2 text-secondary"></i>
              <p class="mb-0">Nenhum gasto do Banco do Brasil encontrado para os filtros selecionados no ano de {{ ano }}.</p>
            </div>

            <!-- Container do Gráfico BarChart Detalhe BB -->
            <div
              v-show="!loading && bbDetalheCollection && bbDetalheCollection.labels && bbDetalheCollection.labels.length > 0"
              style="position: relative; height: 380px; width: 100%;"
            >
              <bar-chart
                v-if="loadedBb"
                :key="'bb-detalhe-' + chartBbDetalheKey"
                :chart-data="bbDetalheCollection"
                :options="chartBbDetalheOptions"
              />
            </div>
          </div>

          <!-- Tabela Analítica BB -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold text-secondary">
                Tabela Analítica BB: Gastos {{ bbVisaoDetalhe === 'descricao' ? 'por Descrição (lan_descricaos)' : 'por Centro de Custo (lan_fiscais)' }} ({{ ano }})
              </h6>
              <div style="width: 240px;">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Buscar na tabela..."
                  v-model="bbFiltroTabelaBusca"
                />
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-hover table-striped align-middle mb-0 text-center">
                <thead class="table-light">
                  <tr style="font-size: 13px;">
                    <th class="text-start ps-3">
                      {{ bbVisaoDetalhe === 'descricao' ? 'Descrição' : 'Centro de Custo' }}
                    </th>
                    <th class="text-center" v-if="bbVisaoDetalhe === 'descricao'">Centro de Custo</th>
                    <th class="text-center">Qtd. Lançamentos</th>
                    <th class="text-end pe-3">Total Gasto</th>
                    <th class="text-end pe-3">% do Total BB</th>
                  </tr>
                </thead>
                <tbody style="font-size: 13px;">
                  <tr v-for="(item, idx) in itensTabelaDetalheBB" :key="idx">
                    <td class="text-start ps-3 font-weight-bold">
                      <span class="badge me-1" :style="{ backgroundColor: getCorBadge(idx) }">&nbsp;</span>
                      {{ item.titulo }}
                    </td>
                    <td class="text-center" v-if="bbVisaoDetalhe === 'descricao'">
                      <span class="badge bg-light text-dark border">{{ item.centro_custo }}</span>
                    </td>
                    <td class="text-center">{{ item.quantidade }}</td>
                    <td class="text-end pe-3 font-weight-bold text-primary">
                      {{ formatarMoeda(item.total) }}
                    </td>
                    <td class="text-end pe-3">
                      <div class="d-flex align-items-center justify-content-end">
                        <span class="me-2 font-weight-bold">{{ item.percentual }}%</span>
                        <div class="progress" style="width: 60px; height: 6px;">
                          <div
                            class="progress-bar bg-primary"
                            role="progressbar"
                            :style="{ width: item.percentual + '%' }"
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="itensTabelaDetalheBB.length === 0">
                    <td :colspan="bbVisaoDetalhe === 'descricao' ? 5 : 4" class="py-3 text-muted">
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-light font-weight-bold" style="font-size: 13px;">
                  <tr>
                    <td class="text-start ps-3">TOTAL DOS GASTOS BB EXIBIDOS</td>
                    <td v-if="bbVisaoDetalhe === 'descricao'"></td>
                    <td class="text-center">{{ totalQtdExibidaBB }}</td>
                    <td class="text-primary text-end pe-3">{{ formatarMoeda(totalSomaExibidaBB) }}</td>
                    <td class="text-end pe-3">{{ totalPercentualExibidoBB }}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import BarChart from './BarChart.js'

export default {
  components: {
    BarChart
  },
  props: ['user'],
  data () {
    const currentYear = new Date().getFullYear().toString()
    return {
      moduloAtivo: 'caixinha', // 'caixinha' ou 'bb'
      ano: currentYear,
      caps_id: '1',
      anosDisponiveis: [
        currentYear,
        (parseInt(currentYear) - 1).toString(),
        (parseInt(currentYear) - 2).toString()
      ],
      capsList: [
        { id: '1', nome: 'Sede' },
        { id: '2', nome: 'Novo Tempo' },
        { id: '5', nome: 'David Capstrano' },
        { id: '6', nome: 'Estação' },
        { id: '7', nome: 'Integração' },
        { id: '9', nome: 'Toninho' },
        { id: '10', nome: 'Esperança' }
      ],
      loaded: false,
      loading: false,
      chartKey: 0,
      mostrarTabelaMensal: true,
      datacollection: null,
      totais: {
        entradas: 0,
        saidas: 0,
        saldo: 0
      },
      dadosMensais: [],

      // Estado do Segundo Gráfico Caixinha (Detalhamento)
      visaoDetalhe: 'descricao', // 'descricao' ou 'centro_custo'
      filtroCentroCusto: '', // '' para todos
      filtroTabelaBusca: '',
      detalhesCruzados: [],
      porDescricaoRaw: { labels: [], valores: [], quantidades: [], total: 0 },
      porCentroCustoRaw: { labels: [], valores: [], quantidades: [], total: 0 },
      detalheCollection: null,
      chartDetalheKey: 0,
      loadedDetalhe: false,

      // Estado do Módulo Banco do Brasil (BB)
      bbRaw: {
        mensal: { labels: [], valores: [], total_ano: 0 },
        por_descricao: { labels: [], valores: [], quantidades: [], total: 0 },
        por_centro_custo: { labels: [], valores: [], quantidades: [], total: 0 },
        detalhes_cruzados: []
      },
      bbVisaoDetalhe: 'descricao', // 'descricao' ou 'centro_custo'
      bbFiltroCentroCusto: '', // '' para todos
      bbFiltroTabelaBusca: '',
      bbMensalCollection: null,
      bbDetalheCollection: null,
      chartBbMensalKey: 0,
      chartBbDetalheKey: 0,
      loadedBb: false,

      // Paleta de cores para o segundo gráfico
      paletaCores: [
        '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
        '#06b6d4', '#ec4899', '#f97316', '#6366f1', '#14b8a6',
        '#84cc16', '#a855f7', '#eab308', '#64748b', '#0ea5e9'
      ],

      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'top',
          labels: {
            boxWidth: 15,
            fontSize: 13,
            fontStyle: 'bold'
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
              const valor = Number(tooltipItem.yLabel) || 0
              const formatado = valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              return datasetLabel + ': ' + formatado
            }
          }
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontStyle: 'bold'
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function (value) {
                return 'R$ ' + Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
              }
            }
          }]
        }
      }
    }
  },
  computed: {
    userJson () {
      if (!this.user) return {}
      return typeof this.user === 'object' ? this.user : JSON.parse(this.user)
    },
    token () {
      const match = document.cookie.split(';').find(item => item.trim().startsWith('token='))
      return match ? 'Bearer ' + match.split('=')[1].trim() : ''
    },
    nomeCapsSelecionado () {
      const caps = this.capsList.find(c => String(c.id) === String(this.caps_id))
      return caps ? caps.nome : ('CAPS ' + this.caps_id)
    },
    listaCentrosCustoUnicos () {
      const lista = new Set()
      if (this.porCentroCustoRaw && this.porCentroCustoRaw.labels) {
        this.porCentroCustoRaw.labels.forEach(c => {
          if (c) lista.add(c)
        })
      }
      if (this.detalhesCruzados) {
        this.detalhesCruzados.forEach(d => {
          if (d.centro_custo) lista.add(d.centro_custo)
        })
      }
      return Array.from(lista).sort()
    },
    chartDetalheOptions () {
      const totalGeral = (this.porDescricaoRaw && this.porDescricaoRaw.total > 0)
        ? this.porDescricaoRaw.total
        : (this.totais.saidas > 0 ? this.totais.saidas : 1)
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const rotulo = data.labels[tooltipItem.index] || ''
              const valor = Number(tooltipItem.yLabel) || 0
              const percentual = ((valor / totalGeral) * 100).toFixed(1)
              const formatado = valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              return `${rotulo}: ${formatado} (${percentual}% das saídas)`
            }
          }
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              autoSkip: false,
              maxRotation: 45,
              minRotation: 0,
              fontStyle: 'bold'
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function (value) {
                return 'R$ ' + Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
              }
            }
          }]
        }
      }
    },
    itensTabelaDetalhe () {
      let itens = []
      const totalGeral = (this.porDescricaoRaw && this.porDescricaoRaw.total > 0)
        ? this.porDescricaoRaw.total
        : (this.totais.saidas > 0 ? this.totais.saidas : 1)

      if (this.visaoDetalhe === 'centro_custo') {
        const labels = this.porCentroCustoRaw.labels || []
        const valores = this.porCentroCustoRaw.valores || []
        const qtds = this.porCentroCustoRaw.quantidades || []
        itens = labels.map((lbl, idx) => {
          const val = valores[idx] || 0
          return {
            titulo: lbl,
            centro_custo: lbl,
            quantidade: qtds[idx] || 0,
            total: val,
            percentual: ((val / totalGeral) * 100).toFixed(1)
          }
        })
      } else {
        if (this.filtroCentroCusto) {
          itens = this.detalhesCruzados
            .filter(d => String(d.centro_custo) === String(this.filtroCentroCusto))
            .map(d => {
              const val = d.total || 0
              return {
                titulo: d.descricao,
                centro_custo: d.centro_custo,
                quantidade: d.quantidade || 0,
                total: val,
                percentual: ((val / totalGeral) * 100).toFixed(1)
              }
            })
        } else {
          const labels = this.porDescricaoRaw.labels || []
          const valores = this.porDescricaoRaw.valores || []
          const qtds = this.porDescricaoRaw.quantidades || []
          itens = labels.map((lbl, idx) => {
            const val = valores[idx] || 0
            return {
              titulo: lbl,
              centro_custo: '-',
              quantidade: qtds[idx] || 0,
              total: val,
              percentual: ((val / totalGeral) * 100).toFixed(1)
            }
          })
        }
      }

      if (this.filtroTabelaBusca) {
        const termo = this.filtroTabelaBusca.toLowerCase().trim()
        itens = itens.filter(i =>
          (i.titulo && i.titulo.toLowerCase().includes(termo)) ||
          (i.centro_custo && i.centro_custo.toLowerCase().includes(termo))
        )
      }

      return itens
    },
    totalQtdExibida () {
      return this.itensTabelaDetalhe.reduce((acc, curr) => acc + (curr.quantidade || 0), 0)
    },
    totalSomaExibida () {
      return this.itensTabelaDetalhe.reduce((acc, curr) => acc + (curr.total || 0), 0)
    },
    totalPercentualExibido () {
      const totalGeral = (this.porDescricaoRaw && this.porDescricaoRaw.total > 0)
        ? this.porDescricaoRaw.total
        : (this.totais.saidas > 0 ? this.totais.saidas : 1)
      return ((this.totalSomaExibida / totalGeral) * 100).toFixed(1)
    },

    // COMPUTED PROPRIEDADES BB
    listaCentrosCustoUnicosBB () {
      const lista = new Set()
      if (this.bbRaw.por_centro_custo && this.bbRaw.por_centro_custo.labels) {
        this.bbRaw.por_centro_custo.labels.forEach(c => {
          if (c) lista.add(c)
        })
      }
      if (this.bbRaw.detalhes_cruzados) {
        this.bbRaw.detalhes_cruzados.forEach(d => {
          if (d.centro_custo) lista.add(d.centro_custo)
        })
      }
      return Array.from(lista).sort()
    },
    chartBbMensalOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const valor = Number(tooltipItem.yLabel) || 0
              return 'Total Gasto: ' + valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            }
          }
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontStyle: 'bold' }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function (value) {
                return 'R$ ' + Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
              }
            }
          }]
        }
      }
    },
    chartBbDetalheOptions () {
      const totalGeral = (this.bbRaw.por_descricao && this.bbRaw.por_descricao.total > 0)
        ? this.bbRaw.por_descricao.total
        : (this.bbRaw.mensal.total_ano > 0 ? this.bbRaw.mensal.total_ano : 1)
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const rotulo = data.labels[tooltipItem.index] || ''
              const valor = Number(tooltipItem.yLabel) || 0
              const pct = ((valor / totalGeral) * 100).toFixed(1)
              const formatado = valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              return `${rotulo}: ${formatado} (${pct}% do total BB)`
            }
          }
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { autoSkip: false, maxRotation: 45, minRotation: 0, fontStyle: 'bold' }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function (value) {
                return 'R$ ' + Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
              }
            }
          }]
        }
      }
    },
    itensTabelaDetalheBB () {
      let itens = []
      const totalGeral = (this.bbRaw.por_descricao && this.bbRaw.por_descricao.total > 0)
        ? this.bbRaw.por_descricao.total
        : (this.bbRaw.mensal.total_ano > 0 ? this.bbRaw.mensal.total_ano : 1)

      if (this.bbVisaoDetalhe === 'centro_custo') {
        const labels = this.bbRaw.por_centro_custo.labels || []
        const valores = this.bbRaw.por_centro_custo.valores || []
        const qtds = this.bbRaw.por_centro_custo.quantidades || []
        itens = labels.map((lbl, idx) => {
          const val = valores[idx] || 0
          return {
            titulo: lbl,
            centro_custo: lbl,
            quantidade: qtds[idx] || 0,
            total: val,
            percentual: ((val / totalGeral) * 100).toFixed(1)
          }
        })
      } else {
        if (this.bbFiltroCentroCusto) {
          itens = (this.bbRaw.detalhes_cruzados || [])
            .filter(d => String(d.centro_custo) === String(this.bbFiltroCentroCusto))
            .map(d => {
              const val = d.total || 0
              return {
                titulo: d.descricao,
                centro_custo: d.centro_custo,
                quantidade: d.quantidade || 0,
                total: val,
                percentual: ((val / totalGeral) * 100).toFixed(1)
              }
            })
        } else {
          const labels = this.bbRaw.por_descricao.labels || []
          const valores = this.bbRaw.por_descricao.valores || []
          const qtds = this.bbRaw.por_descricao.quantidades || []
          itens = labels.map((lbl, idx) => {
            const val = valores[idx] || 0
            return {
              titulo: lbl,
              centro_custo: '-',
              quantidade: qtds[idx] || 0,
              total: val,
              percentual: ((val / totalGeral) * 100).toFixed(1)
            }
          })
        }
      }

      if (this.bbFiltroTabelaBusca) {
        const termo = this.bbFiltroTabelaBusca.toLowerCase().trim()
        itens = itens.filter(i =>
          (i.titulo && i.titulo.toLowerCase().includes(termo)) ||
          (i.centro_custo && i.centro_custo.toLowerCase().includes(termo))
        )
      }

      return itens
    },
    totalQtdExibidaBB () {
      return this.itensTabelaDetalheBB.reduce((acc, curr) => acc + (curr.quantidade || 0), 0)
    },
    totalSomaExibidaBB () {
      return this.itensTabelaDetalheBB.reduce((acc, curr) => acc + (curr.total || 0), 0)
    },
    totalPercentualExibidoBB () {
      const totalGeral = (this.bbRaw.por_descricao && this.bbRaw.por_descricao.total > 0)
        ? this.bbRaw.por_descricao.total
        : (this.bbRaw.mensal.total_ano > 0 ? this.bbRaw.mensal.total_ano : 1)
      return ((this.totalSomaExibidaBB / totalGeral) * 100).toFixed(1)
    }
  },
  mounted () {
    if (this.userJson && this.userJson.caps_id) {
      this.caps_id = String(this.userJson.caps_id)
    }
    this.carregarRelatorio()
  },
  methods: {
    formatarMoeda (valor) {
      if (valor === null || valor === undefined) return 'R$ 0,00'
      return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    getCorBadge (index) {
      return this.paletaCores[index % this.paletaCores.length]
    },
    mudarModulo (modulo) {
      this.moduloAtivo = modulo
      this.$nextTick(() => {
        if (modulo === 'bb') {
          this.atualizarGraficosBB()
        } else {
          this.atualizarGraficoDetalhes()
        }
      })
    },
    alterarVisao (visao) {
      this.visaoDetalhe = visao
      this.atualizarGraficoDetalhes()
    },
    alterarVisaoBB (visao) {
      this.bbVisaoDetalhe = visao
      this.atualizarGraficosBB()
    },
    atualizarGraficoDetalhes () {
      this.loadedDetalhe = false

      let labels = []
      let dados = []

      if (this.visaoDetalhe === 'centro_custo') {
        labels = this.porCentroCustoRaw.labels || []
        dados = this.porCentroCustoRaw.valores || []
      } else {
        if (this.filtroCentroCusto) {
          const filtrados = this.detalhesCruzados.filter(d => String(d.centro_custo) === String(this.filtroCentroCusto))
          labels = filtrados.map(d => d.descricao)
          dados = filtrados.map(d => d.total)
        } else {
          labels = this.porDescricaoRaw.labels || []
          dados = this.porDescricaoRaw.valores || []
        }
      }

      const bgColors = labels.map((_, idx) => this.paletaCores[idx % this.paletaCores.length])

      this.detalheCollection = {
        labels: labels,
        datasets: [
          {
            label: this.visaoDetalhe === 'centro_custo' ? 'Centro de Custo (R$)' : 'Descrição (R$)',
            backgroundColor: bgColors,
            data: dados
          }
        ]
      }

      this.chartDetalheKey++
      this.loadedDetalhe = true
    },
    atualizarGraficosBB () {
      this.loadedBb = false

      // 1. Gráfico Mensal BB
      const mesesNomes = this.bbRaw.mensal.labels || ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
      this.bbMensalCollection = {
        labels: mesesNomes,
        datasets: [
          {
            label: 'Gastos Banco do Brasil (R$)',
            backgroundColor: '#2563eb',
            hoverBackgroundColor: '#1d4ed8',
            data: this.bbRaw.mensal.valores || []
          }
        ]
      }
      this.chartBbMensalKey++

      // 2. Gráfico Detalhes BB
      let labels = []
      let dados = []

      if (this.bbVisaoDetalhe === 'centro_custo') {
        labels = this.bbRaw.por_centro_custo.labels || []
        dados = this.bbRaw.por_centro_custo.valores || []
      } else {
        if (this.bbFiltroCentroCusto) {
          const filtrados = (this.bbRaw.detalhes_cruzados || []).filter(d => String(d.centro_custo) === String(this.bbFiltroCentroCusto))
          labels = filtrados.map(d => d.descricao)
          dados = filtrados.map(d => d.total)
        } else {
          labels = this.bbRaw.por_descricao.labels || []
          dados = this.bbRaw.por_descricao.valores || []
        }
      }

      const bgColors = labels.map((_, idx) => this.paletaCores[idx % this.paletaCores.length])
      this.bbDetalheCollection = {
        labels: labels,
        datasets: [
          {
            label: this.bbVisaoDetalhe === 'centro_custo' ? 'Centro de Custo BB (R$)' : 'Descrição BB (R$)',
            backgroundColor: bgColors,
            data: dados
          }
        ]
      }
      this.chartBbDetalheKey++
      this.loadedBb = true
    },
    async carregarRelatorio () {
      this.loading = true
      this.loaded = false
      this.loadedDetalhe = false
      this.loadedBb = false

      try {
        const config = {
          headers: {
            Accept: 'application/json'
          },
          params: {
            caps_id: this.caps_id,
            ano: this.ano
          }
        }

        if (this.token) {
          config.headers.Authorization = this.token
        }

        const response = await axios.get('/api/v1/relatorios/financeiro', config) 
        const data = response.data.fluxo_caixa

        // Atualiza lista de anos disponíveis dinamicamente se retornado pela API
        if (response.data.anos_disponiveis && response.data.anos_disponiveis.length) {
          const anosSet = new Set([...this.anosDisponiveis, ...response.data.anos_disponiveis])
          this.anosDisponiveis = Array.from(anosSet).sort((a, b) => b.localeCompare(a))
        }

        // Atualiza totais Caixinha (KPIs)
        this.totais = {
          entradas: data.total_entradas || 0,
          saidas: data.total_saidas || 0,
          saldo: data.saldo || 0
        }

        // Prepara tabela mensal do gráfico 1 Caixinha
        const mesesNomes = data.meses_nomes || ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
        this.dadosMensais = (data.labels || []).map((lbl, idx) => {
          const ent = (data.entradas && data.entradas[idx]) ? data.entradas[idx] : 0
          const sai = (data.saidas && data.saidas[idx]) ? data.saidas[idx] : 0
          return {
            rotulo: lbl,
            mesNome: mesesNomes[idx] || lbl,
            entradas: ent,
            saidas: sai,
            saldo: ent - sai
          }
        })

        // Atualiza dados do gráfico 1 Caixinha
        this.datacollection = {
          labels: mesesNomes,
          datasets: [
            {
              label: 'Entradas',
              backgroundColor: '#10b981',
              hoverBackgroundColor: '#059669',
              data: data.entradas || []
            },
            {
              label: 'Saídas',
              backgroundColor: '#ef4444',
              hoverBackgroundColor: '#dc2626',
              data: data.saidas || []
            }
          ]
        }

        // Armazena dados para o segundo gráfico Caixinha (Detalhamento)
        this.porDescricaoRaw = response.data.por_descricao || { labels: [], valores: [], quantidades: [], total: 0 }
        this.porCentroCustoRaw = response.data.por_centro_custo || { labels: [], valores: [], quantidades: [], total: 0 }
        this.detalhesCruzados = response.data.detalhes_cruzados || []

        // Armazena dados para o Módulo Banco do Brasil (BB)
        this.bbRaw = response.data.bb || {
          mensal: { labels: [], valores: [], total_ano: 0 },
          por_descricao: { labels: [], valores: [], quantidades: [], total: 0 },
          por_centro_custo: { labels: [], valores: [], quantidades: [], total: 0 },
          detalhes_cruzados: []
        }

        this.chartKey++
        this.loaded = true

        // Atualiza e monta o segundo gráfico Caixinha
        this.atualizarGraficoDetalhes()

        // Atualiza e monta os gráficos de Banco do Brasil
        this.atualizarGraficosBB()
      } catch (error) {
        console.error('Erro ao carregar dados do gráfico:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.font-weight-bold {
  font-weight: 600;
}
.border-4 {
  border-left-width: 4px !important;
}
.progress {
  background-color: #e5e7eb;
}
.nav-pills .nav-link {
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}
.nav-pills .nav-link.active {
  background-color: #3b82f6 !important;
  color: #fff !important;
}
</style>